import moment from "moment";
import { IDeskArea, IDeskAreaItemUser } from "types";

export const availabilityColors = {
  available: "var(--floorplan-available)",
  busy: "var(--floorplan-busy)",
} as const;

export const meetingRoomIsAvailable = (deskArea: IDeskArea): boolean => {
  const { availability } = deskArea;
  const items = availability?.zoneItems || deskArea.items;
  if (!availability) {
    return true;
  }
  const allBookingData = items.reduce<IDeskAreaItemUser[]>((acc, curr) => [...acc, ...curr.users], []);
  const now = new Date().getTime();
  const currentMeeting = allBookingData.find((u) => u.startTime <= now && now <= u.endTime);
  return !currentMeeting;
};

export const meetingRoomAvailabilityColor = (deskArea: IDeskArea): string => {
  const isAvailable = meetingRoomIsAvailable(deskArea);
  return isAvailable ? availabilityColors.available : availabilityColors.busy;
};

export const meetingRoomAvailabilityTitle = (deskArea: IDeskArea): string => {
  const isAvailable = meetingRoomIsAvailable(deskArea);
  return isAvailable
    ? $localize`:@@desk-area-module|meeting-room-is-available:Available`
    : $localize`:@@desk-area-module|meeting-room-is-not-available:Busy`;
};

export const meetingRoomNextMeeting = (deskArea: IDeskArea, timezone: string, timeFormat: string): string => {
  const { availability } = deskArea;

  if (!availability) {
    return "";
  }
  const allBookingData = availability.zoneItems.reduce<IDeskAreaItemUser[]>((acc, curr) => [...acc, ...curr.users], []);
  if (!allBookingData || allBookingData.length === 0) {
    return "";
  }

  const now = new Date().getTime();

  const currentMeetingIdx = allBookingData.findIndex((u) => u.startTime <= now && now <= u.endTime);
  const currentMeeting = allBookingData[currentMeetingIdx];

  const nextMeeting = allBookingData.find((u) => now <= u.startTime);
  if (!nextMeeting && !currentMeeting) {
    return "";
  }

  if (!currentMeeting) {
    if (!nextMeeting) {
      return "";
    }
    const nextMeetingTime = moment(nextMeeting.startTime).tz(timezone).format(timeFormat);
    return $localize`:@@desk-area-module|meeting-room-next-meeting-available-now:Next meeting at ${nextMeetingTime}`;
  }

  if (!nextMeeting) {
    const currentMeetingEndTime = currentMeeting
      ? findNextFreeTime(allBookingData.slice(currentMeetingIdx), timezone, timeFormat)
      : "";
    return $localize`:@@desk-area-module|meeting-room-next-meeting-busy-now:Available from ${currentMeetingEndTime}`;
  }

  return "";
};

const findNextFreeTime = (bookings: IDeskAreaItemUser[], timezone: string, timeFormat: string): string => {
  const bookingTimes = bookings.map(({ startTime, endTime }) => ({
    startTime,
    endTime,
  }));
  let idx = 0;
  let foundValidTime = false;
  let time = null;
  while (!foundValidTime && idx < bookingTimes.length) {
    const b = bookingTimes[idx];
    if (!time) {
      time = b.endTime;
      idx++;
      continue;
    }
    if (b.startTime > time) {
      foundValidTime = true;
      continue;
    }
    time = b.endTime;
    idx++;
  }

  return moment(time).tz(timezone).format(timeFormat);
};
